import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from 'react-hot-toast'
import { initObservability } from '@ziro/browser-logs'
import { ObservabilityBoundary } from '@ziro/browser-logs/react'
import App from './App'
import { ThemeProvider } from './contexts/ThemeContext'
import { initPerformanceMonitoring } from './utils/performance'
import { initGlobalErrorHandlers } from './lib/error-reporter'
import { logger } from './config/env'
import './styles/index.css'

// Initialize performance monitoring
initPerformanceMonitoring()

// Log app startup
logger.info('AI Agent Platform starting...')

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
})

// Initialize global error reporting to OCS
initGlobalErrorHandlers()

initObservability({
  service: 'agent-dashboard-frontend',
  endpoint: 'https://ocs.ziro.pub/api/v2/logs/frontend',
})

// Use Vite's BASE_URL as the router basename. Locally this is "/", but when
// the app is served under a subpath (ZBox Commander's preview-proxy serves it
// at /api/sessions/<id>/preview-proxy/), Vite is started with --base that
// matches, and BASE_URL reflects it. Navigation (navigate('/login'), etc.)
// then stays within the subpath instead of escaping to the parent origin's
// root.
const routerBasename = import.meta.env.BASE_URL.replace(/\/$/, '') || '/'

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <BrowserRouter basename={routerBasename}>
      <ThemeProvider defaultTheme="system">
      <QueryClientProvider client={queryClient}>
        <ObservabilityBoundary>
          <App />
        </ObservabilityBoundary>
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#363636',
              color: '#fff',
            },
            success: {
              duration: 3000,
              iconTheme: {
                primary: '#22c55e',
                secondary: '#fff',
              },
            },
            error: {
              duration: 5000,
              iconTheme: {
                primary: '#ef4444',
                secondary: '#fff',
              },
            },
          }}
        />
      </QueryClientProvider>
      </ThemeProvider>
    </BrowserRouter>
  </React.StrictMode>
)
